import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import {
  Pill, BookOpen, FileText, Shield, Activity, Microscope,
  CheckCircle2, ArrowRight, Zap, Lock, Globe, Cpu,
  ChevronRight, Sparkles, Database, BarChart3,
} from "lucide-react";

const FadeUp = ({ children, delay = 0, className = "" }: {
  children: React.ReactNode; delay?: number; className?: string;
}) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: "easeOut" }} className={className}>
      {children}
    </motion.div>
  );
};

const MODULES = [
  {
    icon: Pill, color: "text-ay-cyan", bg: "bg-ay-cyan/10", border: "border-ay-cyan/20",
    title: "Vitamin Intelligence Engine",
    tagline: "Built for you. NHS-aligned. Instant.",
    desc: "The only vitamin recommendation engine built on UK clinical data. 15+ rules encoding NHS RDAs, SACN dietary reference values, BNF drug interactions, and UK demographic deficiency patterns.",
    bullets: [
      "Accounts for UK latitude and seasonal UV variation",
      "Ethnicity-specific deficiency risk (South Asian, Black African/Caribbean)",
      "BNF drug-nutrient interactions (metformin, PPIs, anticoagulants)",
      "Pregnancy, breastfeeding, and childbearing-age protocols",
      "Sub-second response — deterministic rule engine, zero LLM cost",
    ],
  },
  {
    icon: BookOpen, color: "text-violet-400", bg: "bg-violet-500/10", border: "border-violet-500/20",
    title: "Literature Q&A",
    tagline: "Ask anything. Get cited UK answers.",
    desc: "Powered by Claude Opus 4.7 with live RAG retrieval from NICE, BNF, NHS pathways, and MHRA guidance. Every answer cites the specific guideline section — no hallucinated references.",
    bullets: [
      "Live retrieval from NICE guidelines (updated monthly)",
      "BNF prescribing and drug interaction queries",
      "MHRA safety updates and device guidance",
      "Multi-turn conversation — follow-up questions in context",
      "Source filter: NICE / BNF / NHS / MHRA / SACN / PubMed",
    ],
  },
  {
    icon: FileText, color: "text-ay-emerald", bg: "bg-ay-emerald/10", border: "border-ay-emerald/20",
    title: "Report Generation",
    tagline: "90% faster. 90% cheaper. Clinical quality.",
    desc: "Generate microbiome health reports, genomic variant reports, and clinical study summaries in seconds. Dual audience mode: clinical depth for GPs, plain English for patients.",
    bullets: [
      "Microbiome health reports with taxa interpretation",
      "Genomic variant reports with ACMG-style classification",
      "Clinical study summaries and CSR sections",
      "Clinician vs consumer audience switching",
      "Export to PDF or DOCX for your workflow",
    ],
  },
  {
    icon: Shield, color: "text-ay-amber", bg: "bg-ay-amber/10", border: "border-ay-amber/20",
    title: "Regulatory Drafting",
    tagline: "NICE submissions in minutes, not months.",
    desc: "Draft NICE submissions, CE technical files, MHRA protocols, and safety narratives. UK regulatory context built in — MHRA, EMA, NICE — not just FDA.",
    bullets: [
      "NICE submission outlines (PMG9 structure)",
      "CE technical files (MDR 2017/745 framework)",
      "Clinical trial protocols (ICH E6(R3) structured)",
      "Safety narratives (ICH E2B(R3) format)",
      "[DATA REQUIRED] markers for expert review sections",
    ],
  },
  {
    icon: Activity, color: "text-rose-400", bg: "bg-rose-500/10", border: "border-rose-500/20",
    title: "Data Interpretation",
    tagline: "Lab results that tell a story.",
    desc: "Interpret biomarker panels, genomic data, and clinical lab results. Combines multiple data points for clinical context — not just reference range flags.",
    bullets: [
      "Iron deficiency anaemia panel interpretation",
      "Microbiome diversity and dysbiosis assessment",
      "Genomic variant clinical significance",
      "NHS referral pathway recommendations",
      "Dual output: clinical depth + patient-friendly plain English",
    ],
  },
  {
    icon: Microscope, color: "text-sky-400", bg: "bg-sky-500/10", border: "border-sky-500/20",
    title: "Report Analysis (Vision)",
    tagline: "Upload any medical document. Get instant insight.",
    desc: "Upload PDFs, lab reports, pathology images, and genomic exports. Our vision-capable model analyses multi-file submissions and returns structured clinical interpretation.",
    bullets: [
      "PDF lab reports and discharge summaries",
      "Pathology and radiology image analysis",
      "Genomic VCF summary interpretation",
      "Microbiome CSV export analysis",
      "Multi-file batch processing (up to 5 files)",
    ],
  },
];

const PLATFORM_FEATURES = [
  { icon: Lock, title: "Zero-trust security", desc: "Cognito auth, IAM least-privilege, no public Lambda endpoints, OAC CloudFront." },
  { icon: Globe, title: "UK data residency", desc: "All data stays in eu-west-2 (London). Never leaves the UK. GDPR by design." },
  { icon: Database, title: "Async job pattern", desc: "Submit → poll architecture. No 29s timeout. Complex queries run up to 15 minutes." },
  { icon: Cpu, title: "Claude Opus 4.7", desc: "Anthropic's most capable model. Prompt caching reduces repeat-query cost by 90%." },
  { icon: BarChart3, title: "Full observability", desc: "CloudWatch dashboards, X-Ray tracing, CloudTrail audit logs (1-year retention)." },
  { icon: Zap, title: "Hybrid architecture", desc: "Rule engine for fast deterministic queries. LLM for complex reasoning. Right tool, right job." },
];

export default function FeaturesPage() {
  return (
    <div className="overflow-hidden">
      {/* Hero */}
      <section className="relative section-pad pt-32 pb-20">
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="orb orb-cyan w-[500px] h-[500px] -top-40 -right-40 opacity-20" />
        <div className="container-ay relative z-10 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="badge-violet mb-6 mx-auto w-fit">Platform features</div>
            <h1 className="text-5xl md:text-6xl font-black text-white mb-6">
              Six modules.<br />
              <span className="text-gradient-cyan">One platform.</span>
            </h1>
            <p className="text-slate-400 text-xl max-w-2xl mx-auto mb-8">
              Every feature built specifically for UK life sciences. NICE-grounded, BNF-aware,
              NHS-aligned — from the first line of code.
            </p>
            <Link to="/app" className="btn-primary text-base px-8 py-3.5 mx-auto">
              Try all features free <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Module deep-dives */}
      <section className="section-pad pt-0">
        <div className="container-ay space-y-24">
          {MODULES.map(({ icon: Icon, color, bg, border, title, tagline, desc, bullets }, i) => (
            <FadeUp key={title} delay={0.1}>
              <div className={`grid lg:grid-cols-2 gap-12 items-center ${i % 2 === 1 ? "lg:grid-flow-dense" : ""}`}>
                {/* Text */}
                <div className={i % 2 === 1 ? "lg:col-start-2" : ""}>
                  <div className={`w-12 h-12 rounded-xl ${bg} border ${border} flex items-center justify-center mb-5`}>
                    <Icon size={22} className={color} />
                  </div>
                  <p className={`text-sm font-semibold ${color} mb-2`}>{tagline}</p>
                  <h2 className="text-3xl font-black text-white mb-4">{title}</h2>
                  <p className="text-slate-400 leading-relaxed mb-6">{desc}</p>
                  <ul className="space-y-2.5">
                    {bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2.5 text-sm text-slate-300">
                        <CheckCircle2 size={15} className={`${color} flex-shrink-0 mt-0.5`} />
                        {b}
                      </li>
                    ))}
                  </ul>
                  <Link to="/app" className={`inline-flex items-center gap-1.5 mt-6 text-sm font-semibold ${color} hover:opacity-80 transition-opacity`}>
                    Try this feature <ChevronRight size={14} />
                  </Link>
                </div>

                {/* Visual card */}
                <div className={i % 2 === 1 ? "lg:col-start-1 lg:row-start-1" : ""}>
                  <div className={`ay-card border ${border} p-8`}>
                    <div className={`w-16 h-16 rounded-2xl ${bg} border ${border} flex items-center justify-center mx-auto mb-6`}>
                      <Icon size={32} className={color} />
                    </div>
                    <div className="text-center">
                      <div className="text-5xl font-black text-white mb-2">
                        {i === 0 ? "<1s" : i === 1 ? "20+" : i === 2 ? "90%" : i === 3 ? "3min" : i === 4 ? "2x" : "5"}
                      </div>
                      <div className="text-ay-muted text-sm">
                        {i === 0 ? "Response time" : i === 1 ? "UK clinical sources" : i === 2 ? "Time saved vs manual" : i === 3 ? "NICE submission draft" : i === 4 ? "Audience modes" : "File formats supported"}
                      </div>
                    </div>
                    <div className={`mt-6 pt-6 border-t ${border} grid grid-cols-2 gap-3`}>
                      {bullets.slice(0, 4).map((b) => (
                        <div key={b} className={`text-xs ${color} opacity-70 flex items-start gap-1`}>
                          <CheckCircle2 size={10} className="flex-shrink-0 mt-0.5" />
                          <span className="text-slate-400">{b.split(" ").slice(0, 4).join(" ")}…</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* Platform features */}
      <section className="section-pad" style={{ background: "rgba(13,18,32,0.6)" }}>
        <div className="container-ay">
          <FadeUp className="text-center mb-14">
            <div className="badge-cyan mb-4">Platform infrastructure</div>
            <h2 className="text-4xl font-black text-white mb-4">
              Enterprise-grade from day one
            </h2>
          </FadeUp>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {PLATFORM_FEATURES.map(({ icon: Icon, title, desc }, i) => (
              <FadeUp key={title} delay={i * 0.06}>
                <div className="ay-card">
                  <div className="w-10 h-10 rounded-lg bg-ay-cyan/10 border border-ay-cyan/20 flex items-center justify-center mb-4">
                    <Icon size={18} className="text-ay-cyan" />
                  </div>
                  <h3 className="font-bold text-white mb-2">{title}</h3>
                  <p className="text-sm text-slate-400">{desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-pad">
        <div className="container-ay text-center">
          <FadeUp>
            <h2 className="text-4xl font-black text-white mb-4">
              Ready to explore every feature?
            </h2>
            <p className="text-slate-400 mb-8 max-w-xl mx-auto">
              Start your free trial today. No credit card required.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/app" className="btn-primary text-base px-8 py-3.5">
                <Sparkles size={16} /> Start free trial
              </Link>
              <Link to="/pricing" className="btn-outline text-base px-8 py-3.5">
                View pricing
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
